// src/bids/BidEdit.tsx

import React, { FC, useState } from 'react';
import {
    Edit,
    SimpleForm,
    Toolbar,
    SaveButton,
    TextInput,
    DateInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    required,
    useNotify,
    useRedirect,
    TopToolbar,
    usePermissions,
    useDelete,
    useRecordContext,
} from 'react-admin';
import {
    Button as MUIButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';

// Normalize permission entries into ['Admin','ProductAdmin',…']
const normalizeRoles = (perms: any): string[] => {
    const arr = Array.isArray(perms) ? perms : perms != null ? [perms] : [];
    return arr.map(p =>
        typeof p === 'number'
            ? (UserRole[p] as unknown as string)
            : String(p)
    );
};

// Custom Delete button: Admin sees confirmation+delete; ProductAdmin sees denied dialog
const CustomDeleteButton: FC = () => {
    const record = useRecordContext<{ id: number }>();
    const { permissions } = usePermissions<any>();
    const roles = normalizeRoles(permissions);
    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');

    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const redirect = useRedirect();
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openDenied, setOpenDenied] = useState(false);

    const handleClick = () => {
        if (isAdmin) setOpenConfirm(true);
        else if (isProductAdmin) setOpenDenied(true);
    };

    const confirmDelete = async () => {
        await deleteOne('bids', { id: record.id });
        notify('Bid deleted', { type: 'info' });
        setOpenConfirm(false);
        redirect('/bids');
    };

    return (
        <>
            <MUIButton
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={handleClick}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
            >
                Delete
            </MUIButton>

            {/* Admin confirmation */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this bid?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MUIButton onClick={() => setOpenConfirm(false)}>
                        Cancel
                    </MUIButton>
                    <MUIButton color="error" onClick={confirmDelete}>
                        Delete
                    </MUIButton>
                </DialogActions>
            </Dialog>

            {/* ProductAdmin denied */}
            <Dialog open={openDenied} onClose={() => setOpenDenied(false)}>
                <DialogTitle>Permission Denied</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You don’t have permission to delete bids. Please contact your administrators.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MUIButton onClick={() => setOpenDenied(false)}>
                        OK
                    </MUIButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Top toolbar: Back + conditional Delete
const BidEditActions: FC<any> = props => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const { permissions } = usePermissions<any>();
    const roles = normalizeRoles(permissions);
    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');
    const redirect = useRedirect();

    return (
        <TopToolbar>
            {(isAdmin || isProductAdmin) && <CustomDeleteButton />}
        </TopToolbar>
    );
};

// Bottom toolbar: Save then Back
const BidEditToolbar: FC<any> = props => {
    const redirect = useRedirect();
    return (
        <Toolbar {...props}>
            <SaveButton />
        </Toolbar>
    );
};

export const BidEdit: FC<any> = props => {
    const notify = useNotify();
    const redirect = useRedirect();

    const onSuccess = () => {
        notify('Bid updated successfully', { type: 'info' });
        redirect('/bids');
    };

    // Existing validators
    const validateDecimal = (message: string) => (value: any) => {
        if (!value) return;
        const regex = /^\d+(\.\d{1,2})?$/;
        return regex.test(value) ? undefined : message;
    };
    const validateInteger = (message: string) => (value: any) => {
        if (value == null || value === '') return;
        return Number.isInteger(Number(value)) ? undefined : message;
    };

    return (
        <Edit
            {...props}
            actions={<BidEditActions />}
            mutationMode="pessimistic"
            onSuccess={onSuccess}
        >
            <SimpleForm toolbar={<BidEditToolbar />}>
                <NumberInput source="id" disabled label="Bid Id" />
                <TextInput source="bid_date" disabled label="Date" />
                <NumberInput source="bid_price" disabled label="Price" />
                <NumberInput
                    source="accepted_bid_quantity"
                    label="Accepted Qty"
                    validate={[required(), validateInteger('Not a valid Qty')]}
                />
                <NumberInput
                    source="accepted_bid_price"
                    label="Accepted Price"
                    validate={[required(), validateDecimal('Not a valid Price')]}
                />
                <ReferenceInput source="bid_status_id" reference="bids/getStatus" alwaysOn>
                    <SelectInput
                        optionText="bid_status"
                        label="Bid Status"
                        validate={[required()]}
                    />
                </ReferenceInput>
            </SimpleForm>
        </Edit>
    );
};

export default BidEdit;
